import React, { useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import { CardActionArea, ButtonBase, Box, useTheme, Rating } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import { useCookies } from 'react-cookie'
import BookDialog from './BookDialog'
import { DEFAULT_BOOK_IMAGE } from '../constants'

const BookCard = ({ refresh, setRefresh, book, onEdit }) => {
  const [open, setOpen] = useState(false)
  const [cookies] = useCookies(['token'])
  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleEdit = () => {
    if (onEdit) {
      onEdit(book)
    }
  }

  return (
    <>
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: 380,
          margin: 'auto',
          marginBottom: 2,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.2s, border-color 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-4px)'
          }
        }}
      >
        <CardActionArea
          onClick={handleOpen}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 324,
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ position: 'relative', width: '100%', height: 160 }}>
            <CardMedia
              component="img"
              image={book.image_url || DEFAULT_BOOK_IMAGE}
              alt={book.title}
              height={160}
              loading="lazy"
              decoding="async"
              sx={{ width: '100%', height: 160, objectFit: 'cover' }}
            />
            {/* Optional overlay gradient for better text contrast if needed later */}
          </Box>
          <CardContent
            sx={{
              height: 164,
              py: 2,
              px: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            {book.series?.name && (
              <Typography variant="caption" color="primary.main" fontWeight="bold" sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {book.series.name} {book.volume_number ? `- Tome ${book.volume_number}` : ''}
              </Typography>
            )}
            <Typography
              variant="h6"
              component="div"
              fontWeight="600"
              title={book.title}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mb: 1,
                lineHeight: 1.3,
                height: 52,
                width: '100%',
                textAlign: 'left',
              }}
            >
              {book.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
                height: 66,
                width: '100%',
                textAlign: 'left',
              }}
            >
              {book.summary && book.summary.length > 150
                ? `${book.summary.slice(0, 150)}...`
                : book.summary}
            </Typography>
          </CardContent>
        </CardActionArea>

        <ButtonBase
          onClick={handleOpen}
          aria-label={`Voir les details`}
          sx={{
            width: '100%',
            px: 2,
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 56,
            textAlign: 'center',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {book.priority !== undefined && book.priority !== null ? (
            <Typography component="span" variant="body1" sx={{ letterSpacing: 2 }}>
              {Array.from({ length: Math.max(0, Math.min(5, Number(book.priority) || 0)) }).map(() => '🔥').join('')}
            </Typography>
          ) : (
            <Rating
              readOnly
              value={Number(book.note_5) || 0}
              icon={<FavoriteIcon fontSize="inherit" sx={{ color: '#e91e63' }} />}
              emptyIcon={<FavoriteBorderIcon fontSize="inherit" />}
              size="small"
            />
          )}
        </ButtonBase>
      </Card>

      <BookDialog
        open={open}
        onClose={handleClose}
        book={book}
        cookies={cookies}
        setRefresh={setRefresh}
        onEdit={handleEdit}
      />
    </>
  )
}

export default BookCard
